import React, { useState, useEffect, useMemo } from 'react';
import {
    TextField,
    Typography,
    Box,
    FormControlLabel,
    Switch,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button
} from "@mui/material";
import { useDispatch, useSelector } from 'react-redux';
import { getFlowConfiguration, updateFlowConfiguration } from '../../Redux/FlowDucks';
import { fetchAssistantsFromAPI } from '../../Redux/GPTServiceDucks';
import { Toast } from 'react-bootstrap';
import { FlowSaveConfirmModal } from './FlowSaveConfirmModal';
import { diffFlow } from '../../Utilities/flowDiff';

export function ConfigurationPanel({ selectedFlow }) {
    const [useAI, setUseAI] = useState(false);
    const [selectedModel, setSelectedModel] = useState('');
    const [firstRetryTime, setFirstRetryTime] = useState(5); // minutos
    const [retryFrequency, setRetryFrequency] = useState(10); // minutos
    const [maxRetries, setMaxRetries] = useState(3); // máximo de reintentos
    const [totalStages, setTotalStages] = useState(1); // cantidad de etapas del flujo

    const [showToast, setShowToast] = useState(false);
    const [message, setMessage] = useState("");
    const [messageBody, setMessageBody] = useState("");
    const [bgColor, setBgColor] = useState("info");
    const [isSaving, setIsSaving] = useState(false);

    const [originalConfigSnapshot, setOriginalConfigSnapshot] = useState(null);
    const [showSaveConfirm, setShowSaveConfirm] = useState(false);
    const [pendingSavePayload, setPendingSavePayload] = useState(null);
    const [pendingSaveDiff, setPendingSaveDiff] = useState(null);

    const configuration = useSelector((state) => state.flow.flowConfiguration);
    const assistants = useSelector((state) => state.gptService.assistants);

    const dispatch = useDispatch();

    useEffect(() => {
        if (selectedFlow) {
            dispatch(getFlowConfiguration(selectedFlow.id))
            dispatch(fetchAssistantsFromAPI());
        }
    }, [selectedFlow, dispatch]);

    useEffect(() => {
        if (configuration) {
            if (configuration.assistantId !== null) {
                setUseAI(true);
            }
            setSelectedModel(configuration.assistantId || '');
            setFirstRetryTime(configuration.firstRetryMinutes || 5);
            setRetryFrequency(configuration.frequencyRetryMinutes || 10);
            setMaxRetries(configuration.maxRetry || 3);
            setTotalStages(configuration.totalStages || 1);
            setOriginalConfigSnapshot({
                assistantId: configuration.assistantId || null,
                firstRetryMinutes: configuration.firstRetryMinutes || 5,
                frequencyRetryMinutes: configuration.frequencyRetryMinutes || 10,
                maxRetry: configuration.maxRetry || 3,
                totalStages: configuration.totalStages || 1,
            });
        }
    }, [configuration]);

    const currentConfigPayload = useMemo(() => {
        return {
            assistantId: useAI ? (selectedModel || null) : null,
            firstRetryMinutes: firstRetryTime,
            frequencyRetryMinutes: retryFrequency,
            maxRetry: maxRetries,
            totalStages,
        };
    }, [useAI, selectedModel, firstRetryTime, retryFrequency, maxRetries, totalStages]);

    const handleSave = () => {
        if (!selectedFlow) return;

        const before = originalConfigSnapshot || {};
        const after = currentConfigPayload;

        // Reutilizamos diffFlow con estructura nodes/edges para tener el mismo modal
        const diff = diffFlow(
            { nodes: [{ id: 'config', data: before }], edges: [] },
            { nodes: [{ id: 'config', data: after }], edges: [] }
        );

        setPendingSavePayload(after);
        setPendingSaveDiff(diff);
        setShowSaveConfirm(true);
    }

    const confirmSave = async () => {
        if (!selectedFlow || !pendingSavePayload) return;
        if (isSaving) return;

        setShowSaveConfirm(false);
        setIsSaving(true);
        try {
            setShowToast(true);
            setBgColor('success');
            setMessage('Guardando configuración');
            setMessageBody('Configuración actualizada correctamente');

            const saved = await dispatch(updateFlowConfiguration(selectedFlow.id, pendingSavePayload));

            // Actualiza snapshot base para próximos diffs
            setOriginalConfigSnapshot({
                assistantId: saved?.assistantId ?? pendingSavePayload.assistantId,
                firstRetryMinutes: saved?.firstRetryMinutes ?? pendingSavePayload.firstRetryMinutes,
                frequencyRetryMinutes: saved?.frequencyRetryMinutes ?? pendingSavePayload.frequencyRetryMinutes,
                maxRetry: saved?.maxRetry ?? pendingSavePayload.maxRetry,
                totalStages: saved?.totalStages ?? pendingSavePayload.totalStages,
            });
        } catch (e) {
            setShowToast(true);
            setBgColor('danger');
            setMessage('Error al guardar');
            setMessageBody('No se pudo guardar la configuración');
        } finally {
            setIsSaving(false);
            setPendingSavePayload(null);
            setPendingSaveDiff(null);
        }
    };

    const cancelSave = () => {
        setShowSaveConfirm(false);
        setPendingSavePayload(null);
        setPendingSaveDiff(null);
    };

    return (
        <Box sx={{ width: '100%', p: 2, overflow: 'auto' }}>
            {selectedFlow ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

                    {/* Cantidad de etapas */}
                    <TextField
                        label="Cantidad de etapas del flujo"
                        type="number"
                        value={totalStages}
                        onChange={(e) => setTotalStages(Math.max(1, Number(e.target.value) || 1))}
                        inputProps={{ min: 1, max: 50 }}
                        fullWidth
                        helperText="Número total de etapas (para reportes y seguimiento)"
                    />

                    {/* Switch para IA */}
                    <FormControlLabel
                        control={<Switch checked={useAI} onChange={(e) => setUseAI(e.target.checked)} color="primary" />}
                        label="Utilizar Inteligencia Artificial"
                    />

                    {/* Select para modelos de IA */}
                    <FormControl fullWidth disabled={!useAI}>
                        <InputLabel id="model-select-label">Modelo de IA</InputLabel>
                        <Select
                            labelId="model-select-label"
                            value={selectedModel}
                            label="Modelo de IA"
                            onChange={(e) => setSelectedModel(e.target.value)}
                        >
                            {assistants && assistants.map((assistant) => (
                                <MenuItem key={assistant.id} value={assistant.id}>
                                    {assistant.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Inputs para tiempo de reintentos */}
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
                        Configuración de Reintentos
                    </Typography>

                    <Box sx={{ display: "flex", gap: 2 }}>
                        <TextField
                            label="Primer Reintento (minutos)"
                            type="number"
                            value={firstRetryTime}
                            onChange={(e) => setFirstRetryTime(Number(e.target.value))}
                            inputProps={{ min: 1, max: 60 }}
                            fullWidth
                            helperText="Tiempo hasta el primer reintento"
                        />
                        <TextField
                            label="Frecuencia de Reintentos (minutos)"
                            type="number"
                            value={retryFrequency}
                            onChange={(e) => setRetryFrequency(Number(e.target.value))}
                            inputProps={{ min: 1, max: 120 }}
                            fullWidth
                            helperText="Intervalo entre reintentos"
                        />
                    </Box>

                    {/* Input para máximo de reintentos */}
                    <TextField
                        label="Máximo de Reintentos por Nodo"
                        type="number"
                        value={maxRetries}
                        onChange={(e) => setMaxRetries(Number(e.target.value))}
                        inputProps={{ min: 1, max: 10 }}
                        fullWidth
                        helperText="Número máximo de intentos antes de marcar como fallido"
                    />

                    <Button variant="contained" color="primary" onClick={handleSave} disabled={isSaving} sx={{ mt: 2, alignSelf: "flex-start" }}>
                        Guardar Cambios
                    </Button>

                    <FlowSaveConfirmModal
                        open={showSaveConfirm}
                        onClose={cancelSave}
                        onConfirm={confirmSave}
                        diff={pendingSaveDiff}
                    />

                    <Toast show={showToast} onClose={() => setShowToast(false)} bg={bgColor} className='position-fixed top-0 end-0 mt-3 me-3' style={{ zIndex: "10000" }} delay={4000} autohide>
                        <Toast.Header> <strong className="me-auto">{message}</strong> </Toast.Header>
                        <Toast.Body> {messageBody} </Toast.Body>
                    </Toast>
                </Box>
            ) : (
                <Typography variant="body2" color="text.secondary">
                    Selecciona un flujo para ver sus copnfiguraciones.
                </Typography>
            )}
        </Box>
    );
}